import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { ScreenshotLightbox } from "../../components/ScreenshotLightbox";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Lifebuoy, MagnifyingGlass, CaretDown, X, ArrowCounterClockwise, Funnel, Paperclip } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ================================
// CANONICAL STATUS CONFIGURATION
// ================================
const STATUS_CONFIG = {
  open: {
    value: "open",
    label: "Open",
    bgColor: "bg-blue-500",
    textColor: "text-white",
    borderColor: "border-blue-500"
  },
  in_progress: {
    value: "in_progress",
    label: "In Progress",
    bgColor: "bg-amber-500",
    textColor: "text-white",
    borderColor: "border-amber-500"
  },
  waiting_on_customer: {
    value: "waiting_on_customer",
    label: "Waiting",
    bgColor: "bg-yellow-500",
    textColor: "text-black",
    borderColor: "border-yellow-500"
  },
  resolved: {
    value: "resolved",
    label: "Resolved",
    bgColor: "bg-emerald-500",
    textColor: "text-white",
    borderColor: "border-emerald-500"
  },
  closed: {
    value: "closed",
    label: "Closed",
    bgColor: "bg-zinc-500",
    textColor: "text-white",
    borderColor: "border-zinc-500"
  }
};

// Default statuses (excludes closed)
const DEFAULT_STATUSES = ["open", "in_progress", "waiting_on_customer", "resolved"];
const ALL_STATUSES = Object.keys(STATUS_CONFIG);

// ================================
// PRIORITY CONFIGURATION
// ================================
const PRIORITY_CONFIG = {
  urgent: {
    value: "urgent",
    label: "Urgent",
    bgColor: "bg-red-500",
    textColor: "text-white",
    order: 0
  },
  high: {
    value: "high",
    label: "High",
    bgColor: "bg-orange-500",
    textColor: "text-white",
    order: 1
  },
  normal: {
    value: "normal",
    label: "Normal",
    bgColor: "bg-zinc-500",
    textColor: "text-white",
    order: 2
  },
  low: {
    value: "low",
    label: "Low",
    bgColor: "bg-zinc-600",
    textColor: "text-white",
    order: 3
  }
};
const ALL_PRIORITIES = Object.keys(PRIORITY_CONFIG);

// ================================
// CATEGORY CONFIGURATION
// ================================
const CATEGORY_CONFIG = {
  billing: { value: "billing", label: "Billing" },
  technical: { value: "technical", label: "Technical" },
  general: { value: "general", label: "General" },
  feature_request: { value: "feature_request", label: "Feature Request" }
};
const ALL_CATEGORIES = Object.keys(CATEGORY_CONFIG);

// ================================
// SORT OPTIONS
// ================================
const SORT_OPTIONS = [
  { value: "oldest", label: "Oldest First" },
  { value: "newest", label: "Newest First" },
  { value: "priority", label: "Priority (High → Low)" }
];

// ================================
// HELPER FUNCTIONS
// ================================
function getStatusConfig(status) {
  return STATUS_CONFIG[status] || {
    value: status || "unknown",
    label: "Unknown",
    bgColor: "bg-zinc-400",
    textColor: "text-white",
    borderColor: "border-zinc-400"
  };
}

function getPriorityConfig(priority) {
  return PRIORITY_CONFIG[priority?.toLowerCase()] || {
    value: priority || "unknown",
    label: "Unknown",
    bgColor: "bg-zinc-400",
    textColor: "text-white",
    order: 99
  };
}

function getCategoryLabel(category) {
  return CATEGORY_CONFIG[category?.toLowerCase()]?.label || category || "Unknown";
}

// ================================
// ACCOUNT LABEL BADGE COMPONENT
// ================================
function AccountLabelBadge({ label }) {
  const isClient = label === "client";
  return (
    <span 
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
        isClient 
          ? "bg-emerald-600 text-white" 
          : "bg-zinc-600 text-zinc-300"
      }`}
      data-testid={`account-label-${label}`}
    >
      {isClient ? "Client" : "Free"}
    </span>
  );
}

// ================================
// MULTI-SELECT DROPDOWN COMPONENT
// ================================
function MultiSelectDropdown({ 
  label, 
  options, 
  selected, 
  onChange, 
  getOptionLabel,
  testId 
}) {
  const [open, setOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const selectAll = () => onChange([...options]);
  const clearAll = () => onChange([]);

  const displayText = selected.length === 0 
    ? `All ${label}` 
    : selected.length === options.length 
      ? `All ${label}` 
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 px-3 bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white justify-between min-w-[140px]"
          data-testid={testId}
        >
          <span className="truncate">{displayText}</span>
          <CaretDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 bg-zinc-900 border-zinc-700" align="start">
        <div className="flex gap-2 mb-2 px-1">
          <button 
            onClick={selectAll}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Select All
          </button>
          <span className="text-zinc-600">|</span>
          <button 
            onClick={clearAll}
            className="text-xs text-zinc-400 hover:text-zinc-300"
          >
            Clear
          </button>
        </div>
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800 cursor-pointer"
            >
              <Checkbox
                checked={selected.includes(option)}
                onCheckedChange={() => toggleOption(option)}
                className="border-zinc-600 data-[state=checked]:bg-[#FF6600] data-[state=checked]:border-[#FF6600]"
              />
              <span className="text-sm text-zinc-300">
                {getOptionLabel(option)}
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ================================
// ACTIVE FILTER CHIP COMPONENT
// ================================
function FilterChip({ label, onRemove, testId }) {
  return (
    <span 
      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-300"
      data-testid={testId}
    >
      {label}
      <button 
        onClick={onRemove}
        className="hover:text-white transition-colors"
        aria-label={`Remove filter: ${label}`}
      >
        <X size={12} />
      </button>
    </span>
  );
}

// ================================
// STATUS BADGE COMPONENT
// ================================
function StatusBadge({ status }) {
  const config = getStatusConfig(status);
  return (
    <span 
      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.textColor}`}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </span>
  );
}

// ================================
// PRIORITY BADGE COMPONENT
// ================================
function PriorityBadge({ priority }) {
  const config = getPriorityConfig(priority);
  return (
    <span 
      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.textColor}`}
      data-testid={`priority-badge-${priority}`}
    >
      {config.label}
    </span>
  );
}

// ================================
// MAIN COMPONENT
// ================================
export default function AdminTickets() {
  // Data state
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filter state - multi-select arrays
  const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_STATUSES);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  // Sort state - single select (default to oldest first)
  const [sortBy, setSortBy] = useState("oldest");
  
  // Search state with debounce
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Track if user has modified filters from default
  const isDefaultFilters = useMemo(() => {
    return (
      selectedStatuses.length === DEFAULT_STATUSES.length &&
      DEFAULT_STATUSES.every(s => selectedStatuses.includes(s)) &&
      selectedPriorities.length === 0 &&
      selectedCategories.length === 0
    );
  }, [selectedStatuses, selectedPriorities, selectedCategories]);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build query params for API
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    // Statuses - if empty array, send nothing (API defaults to exclude closed)
    // If specific statuses selected, send them
    if (selectedStatuses.length > 0 && selectedStatuses.length < ALL_STATUSES.length) {
      params.set("statuses", selectedStatuses.join(","));
    } else if (selectedStatuses.length === ALL_STATUSES.length) {
      // All statuses including closed
      params.set("statuses", ALL_STATUSES.join(","));
    }
    // If default statuses, don't send - API will default exclude closed
    
    if (selectedPriorities.length > 0 && selectedPriorities.length < ALL_PRIORITIES.length) {
      params.set("priorities", selectedPriorities.join(","));
    }
    
    if (selectedCategories.length > 0 && selectedCategories.length < ALL_CATEGORIES.length) {
      params.set("categories", selectedCategories.join(","));
    }
    
    if (debouncedSearch.trim()) {
      params.set("search", debouncedSearch.trim());
    }
    
    params.set("sort", sortBy);
    
    return params.toString();
  }, [selectedStatuses, selectedPriorities, selectedCategories, debouncedSearch, sortBy]);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const queryString = buildQueryParams();
      const url = `${API}/admin/tickets${queryString ? `?${queryString}` : ""}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        let ticketList = data.tickets || [];
        
        // Client-side priority sort if needed
        if (sortBy === "priority") {
          ticketList = ticketList.sort((a, b) => {
            const aOrder = getPriorityConfig(a.priority).order;
            const bOrder = getPriorityConfig(b.priority).order;
            return aOrder - bOrder;
          });
        }
        
        setTickets(ticketList);
        setTotal(data.total || ticketList.length);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, sortBy]);

  // Fetch tickets when filters change
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Reset to default filters
  const resetToDefaults = () => {
    setSelectedStatuses(DEFAULT_STATUSES);
    setSelectedPriorities([]);
    setSelectedCategories([]);
    setSearchQuery("");
  };

  // ================================
  // ACTIVE FILTERS DISPLAY
  // ================================
  const activeFilterChips = useMemo(() => {
    const chips = [];
    
    // Status chips - show what's selected
    if (selectedStatuses.length > 0 && selectedStatuses.length < ALL_STATUSES.length) {
      // Check if it matches default (closed hidden)
      const isDefaultStatus = selectedStatuses.length === DEFAULT_STATUSES.length &&
        DEFAULT_STATUSES.every(s => selectedStatuses.includes(s));
      
      if (isDefaultStatus) {
        chips.push({
          key: "status-default",
          label: "Closed hidden",
          type: "status",
          isDefault: true
        });
      } else {
        const statusLabels = selectedStatuses.map(s => getStatusConfig(s).label).join(", ");
        chips.push({
          key: "status-custom",
          label: `Status: ${statusLabels}`,
          type: "status"
        });
      }
    } else if (selectedStatuses.length === ALL_STATUSES.length) {
      chips.push({
        key: "status-all",
        label: "All statuses (incl. Closed)",
        type: "status"
      });
    } else if (selectedStatuses.length === 0) {
      chips.push({
        key: "status-none",
        label: "No status filter",
        type: "status"
      });
    }
    
    // Priority chips
    if (selectedPriorities.length > 0 && selectedPriorities.length < ALL_PRIORITIES.length) {
      const priorityLabels = selectedPriorities.map(p => getPriorityConfig(p).label).join(", ");
      chips.push({
        key: "priority",
        label: `Priority: ${priorityLabels}`,
        type: "priority"
      });
    }
    
    // Category chips
    if (selectedCategories.length > 0 && selectedCategories.length < ALL_CATEGORIES.length) {
      const categoryLabels = selectedCategories.map(c => getCategoryLabel(c)).join(", ");
      chips.push({
        key: "category",
        label: `Category: ${categoryLabels}`,
        type: "category"
      });
    }
    
    // Search chip
    if (debouncedSearch.trim()) {
      chips.push({
        key: "search",
        label: `Search: "${debouncedSearch}"`,
        type: "search"
      });
    }
    
    return chips;
  }, [selectedStatuses, selectedPriorities, selectedCategories, debouncedSearch]);

  const removeFilter = (chip) => {
    switch (chip.type) {
      case "status":
        if (chip.isDefault) {
          // Removing default = show all including closed
          setSelectedStatuses(ALL_STATUSES);
        } else {
          setSelectedStatuses(DEFAULT_STATUSES);
        }
        break;
      case "priority":
        setSelectedPriorities([]);
        break;
      case "category":
        setSelectedCategories([]);
        break;
      case "search":
        setSearchQuery("");
        break;
      default:
        break;
    }
  };

  // ================================
  // TICKET ACTIONS
  // ================================
  const viewTicket = async (ticketId) => {
    try {
      const response = await fetch(`${API}/admin/tickets/${ticketId}`, { credentials: "include" });
      if (response.ok) {
        setSelectedTicket(await response.json());
        setDetailModalOpen(true);
      }
    } catch (error) {
      toast.error("Failed to load ticket");
    }
  };

  const updateStatus = async (status) => {
    if (!selectedTicket || updatingStatus) return;
    
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${API}/admin/tickets/${selectedTicket.ticket_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        // Close modal immediately on success
        setDetailModalOpen(false);
        setSelectedTicket(null);
        toast.success(status === "closed" ? "Ticket closed" : `Status updated to ${status}`);
        fetchTickets();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Update category
  const updateCategory = async (category) => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${API}/admin/tickets/${selectedTicket.ticket_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ category })
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedTicket(updated);
        toast.success(`Category updated to ${getCategoryLabel(category)}`);
        fetchTickets();
      } else {
        toast.error("Failed to update category");
      }
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  // Update priority
  const updatePriority = async (priority) => {
    if (!selectedTicket) return;
    
    try {
      const response = await fetch(`${API}/admin/tickets/${selectedTicket.ticket_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ priority })
      });
      if (response.ok) {
        const updated = await response.json();
        setSelectedTicket(updated);
        toast.success(`Priority updated to ${getPriorityConfig(priority).label}`);
        fetchTickets();
      } else {
        toast.error("Failed to update priority");
      }
    } catch (error) {
      toast.error("Failed to update priority");
    }
  };

  const submitReply = async () => {
    if (!replyContent.trim()) return;
    try {
      await fetch(`${API}/admin/tickets/${selectedTicket.ticket_id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: replyContent })
      });
      toast.success("Reply sent");
      setReplyContent("");
      viewTicket(selectedTicket.ticket_id);
    } catch (error) {
      toast.error("Failed to send reply");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-tickets">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white uppercase">Support Tickets</h1>
      </div>

      {/* ================================ */}
      {/* UNIFIED FILTER BAR */}
      {/* ================================ */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 overflow-x-auto">
            {/* Search Input */}
            <div className="relative flex-shrink-0 w-56">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tickets..."
                className="pl-9 h-9 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="ticket-search"
              />
            </div>

            <div className="h-6 w-px bg-zinc-700 flex-shrink-0" />

            {/* Status Multi-Select */}
            <MultiSelectDropdown
              label="Status"
              options={ALL_STATUSES}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              getOptionLabel={(s) => getStatusConfig(s).label}
              testId="filter-status"
            />

            {/* Priority Multi-Select */}
            <MultiSelectDropdown
              label="Priority"
              options={ALL_PRIORITIES}
              selected={selectedPriorities}
              onChange={setSelectedPriorities}
              getOptionLabel={(p) => getPriorityConfig(p).label}
              testId="filter-priority"
            />

            {/* Category Multi-Select */}
            <MultiSelectDropdown
              label="Category"
              options={ALL_CATEGORIES}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              getOptionLabel={(c) => getCategoryLabel(c)}
              testId="filter-category"
            />

            <div className="h-6 w-px bg-zinc-700 flex-shrink-0" />

            {/* Sort Dropdown (Single Select) */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger 
                className="h-9 w-[170px] flex-shrink-0 bg-zinc-900 border-zinc-700 text-zinc-300" 
                data-testid="sort-select"
                title={SORT_OPTIONS.find(o => o.value === sortBy)?.label}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ================================ */}
      {/* ACTIVE FILTERS DISPLAY */}
      {/* ================================ */}
      <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
        <Funnel size={14} className="text-zinc-500" />
        <span className="text-xs text-zinc-500 mr-1">Filters:</span>
        
        {activeFilterChips.map((chip) => (
          <FilterChip
            key={chip.key}
            label={chip.label}
            onRemove={() => removeFilter(chip)}
            testId={`filter-chip-${chip.key}`}
          />
        ))}
        
        {!isDefaultFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetToDefaults}
            className="h-7 px-2 text-xs text-zinc-400 hover:text-white"
            data-testid="reset-filters-btn"
          >
            <ArrowCounterClockwise size={12} className="mr-1" />
            Reset to Defaults
          </Button>
        )}
      </div>

      {/* Results Count */}
      <div className="text-sm text-zinc-500">
        Showing {tickets.length} ticket{tickets.length !== 1 ? "s" : ""}
      </div>

      {/* ================================ */}
      {/* TICKET LIST */}
      {/* ================================ */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-zinc-800 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Lifebuoy size={48} className="mx-auto mb-4 opacity-50" />
          <p>No tickets match your filters</p>
          <Button
            variant="link"
            onClick={resetToDefaults}
            className="mt-2 text-[#FF6600] hover:text-[#CC5200]"
          >
            Reset filters
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <Card
              key={ticket.ticket_id}
              className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-zinc-700 transition-colors"
              onClick={() => viewTicket(ticket.ticket_id)}
              data-testid={`ticket-card-${ticket.ticket_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center rounded-sm flex-shrink-0">
                      <Lifebuoy size={20} className="text-zinc-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-white truncate flex items-center gap-2">
                        {ticket.subject}
                        {ticket.screenshot?.full_url && (
                          <Paperclip size={14} className="text-zinc-500 flex-shrink-0" title="Has screenshot" />
                        )}
                      </p>
                      <p className="text-sm text-zinc-500 truncate flex items-center gap-2">
                        <span>{ticket.tenant?.company_name}</span>
                        <AccountLabelBadge label={ticket.tenant?.account_label} />
                        <span>• {ticket.user_name}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <PriorityBadge priority={ticket.priority} />
                    <Badge variant="secondary" className="text-xs bg-zinc-800 text-zinc-300 border-zinc-700">
                      {getCategoryLabel(ticket.category)}
                    </Badge>
                    <StatusBadge status={ticket.status} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ================================ */}
      {/* DETAIL MODAL - Gold Standard Styling */}
      {/* ================================ */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {/* Modal Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-800">
            <DialogTitle className="font-heading text-lg uppercase tracking-tight">
              {selectedTicket?.subject}
            </DialogTitle>
            {/* Tenant info with account label */}
            {selectedTicket?.tenant && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-zinc-400">{selectedTicket.tenant.company_name}</span>
                <AccountLabelBadge label={selectedTicket.tenant.account_label} />
              </div>
            )}
          </DialogHeader>
          
          {selectedTicket && (
            <div className="p-6 space-y-6">
              {/* Priority/Category/Status Controls */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Priority Dropdown */}
                <Select value={selectedTicket.priority} onValueChange={updatePriority}>
                  <SelectTrigger 
                    className="w-[120px] h-8 bg-zinc-800 border-zinc-700 text-sm text-zinc-200"
                    data-testid="priority-select"
                  >
                    <SelectValue>{getPriorityConfig(selectedTicket.priority).label}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {getPriorityConfig(p).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Category Dropdown */}
                <Select value={selectedTicket.category} onValueChange={updateCategory}>
                  <SelectTrigger 
                    className="w-[140px] h-8 bg-zinc-800 border-zinc-700 text-sm text-zinc-200"
                    data-testid="category-select"
                  >
                    <SelectValue>{getCategoryLabel(selectedTicket.category)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {getCategoryLabel(cat)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Status Dropdown (Open/Closed/Waiting lifecycle) */}
                <Select value={selectedTicket.status} onValueChange={updateStatus} disabled={updatingStatus}>
                  <SelectTrigger 
                    className="w-[150px] h-8 bg-zinc-800 border-zinc-700 text-sm text-zinc-200"
                    data-testid="status-select"
                  >
                    <SelectValue>
                      {updatingStatus ? "Updating..." : getStatusConfig(selectedTicket.status).label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {getStatusConfig(status).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Original Ticket Description */}
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                  {selectedTicket.description}
                </p>
                <p className="text-xs text-zinc-500 mt-3 pt-3 border-t border-zinc-700/50">
                  {selectedTicket.user_name} • {new Date(selectedTicket.created_at).toLocaleString()}
                </p>
              </div>
              
              {/* Screenshot Display */}
              {selectedTicket.screenshot?.full_url && (
                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                  <p className="text-xs font-medium text-zinc-400 mb-3 flex items-center gap-1.5">
                    <Paperclip size={14} />
                    Attached Screenshot
                  </p>
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    className="block text-left"
                    data-testid="screenshot-thumbnail-btn"
                  >
                    <img
                      src={`${API.replace('/api', '')}${selectedTicket.screenshot.thumb_url || selectedTicket.screenshot.full_url}`}
                      alt="Screenshot"
                      loading="lazy"
                      width={selectedTicket.screenshot.thumb_width}
                      height={selectedTicket.screenshot.thumb_height}
                      className="max-w-full max-h-64 rounded-lg border border-zinc-700 hover:opacity-90 transition-opacity cursor-pointer"
                      style={{
                        aspectRatio: selectedTicket.screenshot.thumb_width && selectedTicket.screenshot.thumb_height 
                          ? `${selectedTicket.screenshot.thumb_width}/${selectedTicket.screenshot.thumb_height}` 
                          : 'auto'
                      }}
                    />
                  </button>
                  <p className="text-xs text-zinc-500 mt-2">
                    Click to view full size
                    {selectedTicket.screenshot.filename && (
                      <span className="ml-2">• {selectedTicket.screenshot.filename}</span>
                    )}
                  </p>
                </div>
              )}
              
              {/* Replies Thread */}
              {selectedTicket.replies?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
                    Conversation ({selectedTicket.replies.length})
                  </p>
                  {selectedTicket.replies.map((reply, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        reply.is_admin 
                          ? "bg-blue-950/30 border-blue-800/50" 
                          : "bg-zinc-800/50 border-zinc-700/50"
                      }`}
                    >
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                        {reply.content}
                      </p>
                      <p className="text-xs text-zinc-500 mt-3 pt-3 border-t border-zinc-700/30">
                        {reply.user_name} • {new Date(reply.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Input */}
              <div className="space-y-3 pt-2 border-t border-zinc-700/50">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 min-h-[100px] resize-none"
                  rows={4}
                  data-testid="reply-textarea"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={submitReply}
                    disabled={!replyContent.trim()}
                    className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-6"
                    data-testid="send-reply-btn"
                  >
                    Send Reply
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Screenshot Lightbox - uses full_url for full-size view */}
      {selectedTicket?.screenshot?.full_url && (
        <ScreenshotLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageUrl={`${API.replace('/api', '')}${selectedTicket.screenshot.full_url}`}
          filename={selectedTicket.screenshot.filename}
        />
      )}
    </div>
  );
}
